import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Plug, RefreshCw, Power, PowerOff } from 'lucide-react'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/error'
import { allowsMultipleAccounts, getCRMIntegrations, getPMSIntegrations, getCalendarIntegrations, INTEGRATION_DEFINITIONS, type IntegrationType } from '@/lib/integrations'
import { IntegrationCard } from '@/components/integrations/IntegrationCard'
import { IntegrationStatusBadge } from '@/components/integrations/IntegrationStatusBadge'
import { HeroCRMDialog } from '@/components/integrations/HeroCRMDialog'
import { GoogleCalendarDialog } from '@/components/integrations/GoogleCalendarDialog'
import { GenericIntegrationDialog, type GenericIntegrationFormData } from '@/components/integrations/GenericIntegrationDialog'
import { useIntegrations } from '@/hooks/useIntegrations'
import { useIntegrationMutations } from '@/hooks/useIntegrationMutations'
import { useGoogleCalendarLinks, type CalendarLink } from '@/hooks/useGoogleCalendar'
import { useGoogleCalendarMutations } from '@/hooks/useGoogleCalendarMutations'
import { EditButton, DeleteButton } from '@/components/ui/action-buttons'
import { Badge } from '@/components/ui/badge'
import type { Integration } from '@/hooks/useIntegrations'
import {
  PageContainer,
  PageCard,
  PageHeader,
  PageHeaderContent,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageContent,
} from '@/components/ui/page'

const safeColor = (color: string | undefined, fallback = '#4285f4') =>
  /^#[0-9a-fA-F]{3,8}$/.test(color ?? '') ? color! : fallback

export const Route = createFileRoute('/integrations')({
  component: IntegrationsPage,
  errorComponent: RouteErrorFallback,
})

function IntegrationsPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedIntegrationType, setSelectedIntegrationType] = useState<IntegrationType>('hero')
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)

  // Google Calendar state
  const [googleDialogOpen, setGoogleDialogOpen] = useState(false)
  const [googleDialogMode, setGoogleDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedLink, setSelectedLink] = useState<CalendarLink | null>(null)

  const { data: integrations = [], isLoading } = useIntegrations()
  const {
    createIntegration,
    updateIntegration,
    deleteIntegration,
    toggleIntegration,
    pollNow,
  } = useIntegrationMutations()

  const { data: calendarLinks = [], isLoading: isLoadingLinks } = useGoogleCalendarLinks()
  const { deleteLink, triggerSync, updateLink } = useGoogleCalendarMutations()

  const crmIntegrations = getCRMIntegrations()
  const pmsIntegrations = getPMSIntegrations()
  const calendarIntegrations = getCalendarIntegrations()

  const handleConfigureIntegration = (type: IntegrationType) => {
    if (type === 'google_calendar') {
      setSelectedLink(null)
      setGoogleDialogMode('create')
      setGoogleDialogOpen(true)
      return
    }
    const existing = allowsMultipleAccounts(type)
      ? undefined
      : integrations.find((i) => i.type === type)
    if (existing) {
      setSelectedIntegration(existing)
      setDialogMode('edit')
    } else {
      setSelectedIntegration(null)
      setDialogMode('create')
    }
    setSelectedIntegrationType(type)
    setDialogOpen(true)
  }

  const handleEditIntegration = (integration: Integration) => {
    setSelectedIntegration(integration)
    setSelectedIntegrationType(integration.type as IntegrationType)
    setDialogMode('edit')
    setDialogOpen(true)
  }

  const handleEditLink = (link: CalendarLink) => {
    setSelectedLink(link)
    setGoogleDialogMode('edit')
    setGoogleDialogOpen(true)
  }

  const handleToggleSync = async (link: CalendarLink) => {
    await updateLink.mutateAsync({ id: link.id, data: { sync_enabled: !link.sync_enabled } })
  }

  const handleSyncNow = async (linkId: string) => {
    await triggerSync.mutateAsync(linkId)
  }

  const handleSubmitHeroCRM = async (data: any) => {
    const integrationData: any = {
      name: data.name,
      type: 'hero',
      is_active: true, // Always reactivate when saving
      config: {
        base_url: data.base_url,
        event_type_mapping: data.event_type_mapping,
        sync_config: {
          entities: data.entities,
          direction: data.direction,
          polling_interval_minutes: data.polling_interval_minutes,
        },
      },
    }

    // Merge cleaned credentials (dialog already filters out placeholders)
    if (data.credentials) {
      for (const [key, value] of Object.entries(data.credentials)) {
        if (value && (value as string).trim()) {
          integrationData[key] = value
        }
      }
    }

    if (dialogMode === 'create') {
      await createIntegration.mutateAsync(integrationData)
    } else if (selectedIntegration) {
      // Update will reactivate if it was deactivated
      await updateIntegration.mutateAsync({
        id: selectedIntegration.id,
        data: integrationData,
      })
    }
  }

  const handleSubmitGeneric = async (data: GenericIntegrationFormData) => {
    const definition = INTEGRATION_DEFINITIONS[selectedIntegrationType]
    const integrationData: any = {
      name: data.name,
      type: selectedIntegrationType,
      is_active: true,
      config: {
        sync_config: {
          entities: definition.supportedEntities,
          direction: definition.supportedDirections[0],
          polling_interval_minutes: definition.defaultPollingInterval,
        },
      },
    }

    // Only include credentials if any were provided (edit mode omits unchanged fields)
    if (Object.keys(data.credentials).length > 0) {
      integrationData.credentials = data.credentials
    }

    if (dialogMode === 'create') {
      await createIntegration.mutateAsync(integrationData)
    } else if (selectedIntegration) {
      await updateIntegration.mutateAsync({
        id: selectedIntegration.id,
        data: integrationData,
      })
    }
  }

  const handleToggleActive = async (id: string, currentlyActive: boolean) => {
    try {
      await toggleIntegration.mutateAsync({ id, isActive: !currentlyActive })
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  const handlePollNow = async (id: string) => {
    await pollNow.mutateAsync(id)
  }

  const countConfigured = (type: IntegrationType) =>
    integrations.filter((i) => i.type === type && i.is_active !== false).length

  const getIntegrationStatus = (integration: Integration) => {
    if (!integration.is_active) return 'inactive'
    // Add logic for syncing, error states based on sync status
    return 'active'
  }

  return (
    <>
      <PageContainer>
        <PageCard>
          <PageHeader>
            <PageHeaderContent>
              <PageHeaderTitle>
                <div className="flex items-center gap-2">
                  <Plug className="h-5 w-5" />
                  <PageHeaderHeading>Integrationen</PageHeaderHeading>
                </div>
                <PageHeaderDescription>
                  Verbinden Sie Audavo mit externen Systemen für nahtlose Datensynchronisation
                </PageHeaderDescription>
              </PageHeaderTitle>
            </PageHeaderContent>
          </PageHeader>
          <PageContent className="p-6 overflow-auto">
            <Tabs defaultValue="available" className="w-full">
              {/* Beide Beschriftungen zusammen sind breiter als ein Handy-Viewport;
                  unter sm laufen daher Kurzfassungen, ab sm die vollen Labels. */}
              <TabsList>
                <TabsTrigger value="available">
                  <span className="sm:hidden">Verfügbar</span>
                  <span className="hidden sm:inline">Verfügbare Integrationen</span>
                </TabsTrigger>
                <TabsTrigger value="configured">
                  <span className="sm:hidden">Konfiguriert</span>
                  <span className="hidden sm:inline">Konfigurierte Integrationen</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="available" className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">CRM</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {crmIntegrations.map((definition) => (
                      <IntegrationCard
                        key={definition.type}
                        definition={definition}
                        configuredCount={countConfigured(definition.type)}
                        onConfigure={() => handleConfigureIntegration(definition.type)}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">PMS</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pmsIntegrations.map((definition) => (
                      <IntegrationCard
                        key={definition.type}
                        definition={definition}
                        configuredCount={countConfigured(definition.type)}
                        onConfigure={() => handleConfigureIntegration(definition.type)}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Kalender</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {calendarIntegrations.map((definition) => {
                      const configured = calendarLinks.some(l => l.provider === definition.type)
                      return (
                        <IntegrationCard
                          key={definition.type}
                          definition={definition}
                          configuredCount={configured ? 1 : 0}
                          onConfigure={() => handleConfigureIntegration(definition.type)}
                        />
                      )
                    })}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="configured" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Verbundene Konten</CardTitle>
                    <CardDescription>
                      Verwalten Sie Ihre konfigurierten Integrationen und überwachen Sie den Synchronisationsstatus
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="text-center py-8 text-muted-foreground">Laden...</div>
                    ) : integrations.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        Keine Integrationen konfiguriert
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Typ</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Letzte Synchronisation</TableHead>
                            <TableHead className="text-right">Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {integrations.map((integration) => {
                            const definition = INTEGRATION_DEFINITIONS[integration.type as IntegrationType]
                            return (
                              <TableRow key={integration.id}>
                                <TableCell className="font-medium">{integration.name}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {definition?.logoUrl ? (
                                      <img src={definition.logoUrl} alt={definition.name} className="h-5 w-auto" />
                                    ) : (
                                      <Badge variant="outline">{integration.type}</Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <IntegrationStatusBadge status={getIntegrationStatus(integration)} />
                                </TableCell>
                                <TableCell>
                                  {(integration as any).last_sync_at
                                    ? new Date((integration as any).last_sync_at).toLocaleString('de-DE')
                                    : 'Nie'}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handlePollNow(integration.id)}
                                      title="Jetzt synchronisieren"
                                      disabled={!integration.is_active}
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleToggleActive(integration.id, integration.is_active)}
                                      title={integration.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                    >
                                      {integration.is_active ? (
                                        <Power className="h-4 w-4 text-green-600" />
                                      ) : (
                                        <PowerOff className="h-4 w-4 text-gray-400" />
                                      )}
                                    </Button>
                                    <EditButton onClick={() => handleEditIntegration(integration)} />
                                    {integration.is_active && (
                                      <DeleteButton
                                        onDelete={() => deleteIntegration.mutateAsync(integration.id)}
                                        itemName={integration.name}
                                        confirmationTitle="Integration deaktivieren"
                                        confirmationDescription={`Sind Sie sicher, dass Sie "${integration.name}" deaktivieren möchten? Sie können die Integration später wieder aktivieren.`}
                                      >
                                        Deaktivieren
                                      </DeleteButton>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <img src="/integration-logos/calendar/google-calendar.svg" alt="Google Kalender" className="h-4 w-auto" />
                      Google Kalender Verknüpfungen
                    </CardTitle>
                    <CardDescription>
                      Verwalten Sie Ihre Google Kalender Verknüpfungen
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingLinks ? (
                      <div className="text-center py-8 text-muted-foreground">Laden...</div>
                    ) : calendarLinks.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        Keine Google Kalender Verknüpfungen konfiguriert
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Google Kalender</TableHead>
                            <TableHead>Sync Status</TableHead>
                            <TableHead>Letzte Synchronisation</TableHead>
                            <TableHead className="text-right">Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {calendarLinks.map((link) => (
                            <TableRow key={link.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {link.color && (
                                    <div
                                      className="w-3 h-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: safeColor(link.color) }}
                                    />
                                  )}
                                  <span className="font-medium">{link.name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {link.provider_calendar_id}
                              </TableCell>
                              <TableCell>
                                <IntegrationStatusBadge
                                  status={link.sync_enabled ? 'active' : 'inactive'}
                                />
                              </TableCell>
                              <TableCell>
                                {link.last_synced_at
                                  ? new Date(link.last_synced_at).toLocaleString('de-DE')
                                  : 'Nie'}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleSyncNow(link.id)}
                                    title="Jetzt synchronisieren"
                                    disabled={!link.sync_enabled}
                                  >
                                    <RefreshCw className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleToggleSync(link)}
                                    title={link.sync_enabled ? 'Deaktivieren' : 'Aktivieren'}
                                  >
                                    {link.sync_enabled ? (
                                      <Power className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <PowerOff className="h-4 w-4 text-gray-400" />
                                    )}
                                  </Button>
                                  <EditButton onClick={() => handleEditLink(link)} />
                                  <DeleteButton
                                    onDelete={() => deleteLink.mutateAsync(link.id)}
                                    itemName={link.name}
                                    confirmationTitle="Verknüpfung löschen"
                                    confirmationDescription={`Sind Sie sicher, dass Sie die Verknüpfung "${link.name}" löschen möchten?`}
                                  >
                                    Löschen
                                  </DeleteButton>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </PageContent>
        </PageCard>
      </PageContainer>

      {/* Hero CRM Dialog */}
      {selectedIntegrationType === 'hero' && (
        <HeroCRMDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSubmit={handleSubmitHeroCRM}
          mode={dialogMode}
          integrationId={selectedIntegration?.id}
          initialData={
            selectedIntegration
              ? {
                  name: selectedIntegration.name,
                  base_url: selectedIntegration.config.base_url,
                  entities: selectedIntegration.config.sync_config.entities,
                  direction: selectedIntegration.config.sync_config.direction,
                  polling_interval_minutes: selectedIntegration.config.sync_config.polling_interval_minutes,
                  event_type_mapping: selectedIntegration.config.event_type_mapping,
                }
              : undefined
          }
        />
      )}

      {/* Generic Integration Dialog (Zimmersoftware, future types) */}
      {selectedIntegrationType !== 'hero' && selectedIntegrationType !== 'google_calendar' && (
        <GenericIntegrationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSubmit={handleSubmitGeneric}
          definition={INTEGRATION_DEFINITIONS[selectedIntegrationType]}
          mode={dialogMode}
          integrationId={selectedIntegration?.id}
          initialData={
            selectedIntegration
              ? { name: selectedIntegration.name }
              : undefined
          }
        />
      )}

      {/* Google Calendar Dialog */}
      <GoogleCalendarDialog
        open={googleDialogOpen}
        onOpenChange={setGoogleDialogOpen}
        mode={googleDialogMode}
        existingLink={selectedLink}
      />
    </>
  )
}
